/**
 * Constants for the Stripe Express Checkout Element on the cart page.
 */

/**
 * Stripe.js v3 CDN URL. Update here if Stripe releases a new major version.
 */
export const STRIPE_JS_URL = 'https://js.stripe.com/v3/';

/**
 * Marks injected Stripe.js tags so we don't remove scripts from other consumers (e.g. Spreedly,
 * checkout SPA). Mirrors `RP_STRIPE_SCRIPT_ATTR` from `checkout/client/lib/rp-payments/stripe-confirmation-token.js`.
 */
export const RP_STRIPE_SCRIPT_ATTR = 'data-rp-payments-stripe';

/**
 * Webstore PHP endpoints servicing the wallet sheet. Both are POSTs that ride the webstore CSRF
 * token attached automatically to same-origin AJAX -- no Checkout-owned cookie is ever set on the
 * cart page.
 *
 *   start    -- Creates a CheckoutSession and returns the shipping/confirm URLs (Node `checkout/`
 *               controllers) the wallet sheet then talks to directly.
 *   persist  -- Writes the wallet's buyer email + shipping/billing addresses (the PII Stripe only
 *               reveals at authorization) onto the order, just before the Node completion GET. This
 *               keeps that PII out of the completion URL's query string.
 */
export const ENDPOINTS = {
	persist: '/ajax/checkout/express/persist',
	start: '/ajax/checkout/express/start',
};

/**
 * Internal API delivery types we filter out of the wallet sheet's rate list. Pickup options can't be
 * fulfilled by a digital wallet (no shipping address selected), and the shopper would have nowhere to
 * confirm the pickup location -- matches the PayPal smart-button filter in
 * `webstore/public/js/components/paypal-smart-buttons/paypal-constants.js`.
 */
export const PICKUP_DELIVERY_TYPES = ['Pickup'];

/**
 * Fallback ISO 4217 currency code (uppercase) when no cart or env currency is available.
 * Matches {@link DEFAULT_CURRENCY_CODE} in `paypal-smart-buttons/paypal-constants.js`.
 */
export const DEFAULT_CURRENCY_CODE = 'USD';

/**
 * @param {unknown} value
 * @returns {string | null} Uppercase ISO 4217 code, or null when missing/invalid.
 */
function normalizeCurrencyCode(value) {
	if (typeof value !== 'string' || value === '') {
		return null;
	}
	return value.toUpperCase();
}

/**
 * Returns the store currency for Stripe ECE and wallet-sheet payloads. Resolution order mirrors
 * PayPal cart wallets (Apple Pay / Google Pay use `rp_app.cart._currency_code`):
 *
 *   1. `rp_app.cart._currency_code` — live cart currency (updated on cart AJAX refresh)
 *   2. `rp_app.env.rp_payments.currency` — page-render snapshot from CartPageController
 *   3. {@link DEFAULT_CURRENCY_CODE}
 *
 * @returns {string} Uppercase ISO 4217 code (e.g. `'USD'`).
 */
export function getWalletCurrencyCode() {
	if (typeof window === 'undefined') {
		return DEFAULT_CURRENCY_CODE;
	}

	const cartCurrency = normalizeCurrencyCode(window.rp_app?.cart?._currency_code);
	if (cartCurrency) {
		return cartCurrency;
	}

	const envCurrency = normalizeCurrencyCode(window.rp_app?.env?.rp_payments?.currency);
	if (envCurrency) {
		return envCurrency;
	}

	return DEFAULT_CURRENCY_CODE;
}

/**
 * Maps RP gateway-settings wallet tokens to Stripe ECE `paymentMethods` keys.
 *
 * Matches the same map in `checkout/client/lib/rp-payments/stripe-confirmation-token.js`. Apple Pay
 * and Google Pay use `'always'` because we render the explicit button; Link uses `'auto'` because it
 * only renders when the buyer has an existing Link account, which is the desired UX.
 */
export const WALLET_TOKEN_MAP = {
	apple_pay: { key: 'applePay', visibility: 'always' },
	google_pay: { key: 'googlePay', visibility: 'always' },
	link: { key: 'link', visibility: 'auto' },
};
